import type { CardPhrases } from './order-card-phrases';

// Tom sträng, aldrig nyckeln: CSS:en versaliserar, så ett omappat steg hade
// lyst "OUT_FOR_DELIVERY" mitt i kortet. Rutan behåller sin plats i följden.
export const timelineStepLabel = (step: string, phrases: CardPhrases): string =>
  phrases.steps[step] ?? '';

export const formatOrderHeading = (orderNumber: string, phrases: CardPhrases): string =>
  [phrases.order, String(orderNumber ?? '').trim()].filter(Boolean).join(' ');

export const formatCardDate = (value?: string): string => {
  const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '';
};

export const formatCardTime = (value?: string): string => {
  const date = new Date(String(value ?? ''));
  if (!Number.isFinite(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const monthName = (monthIndex: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { month: 'long' }).format(
    new Date(Date.UTC(2026, monthIndex, 15)),
  );

export const formatDateWindow = (
  startValue: string | undefined,
  endValue: string | undefined,
  locale: string,
): string => {
  const start = String(startValue ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!start) return '';
  const end = String(endValue ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/) ?? start;
  const startDay = Number(start[3]);
  const endDay = Number(end[3]);
  const startMonth = monthName(Number(start[2]) - 1, locale);
  const endMonth = monthName(Number(end[2]) - 1, locale);
  if (start[2] === end[2] && start[3] === end[3]) return `${startDay} ${startMonth}`;
  if (start[2] === end[2]) return `${startDay}–${endDay} ${startMonth}`;
  return `${startDay} ${startMonth}–${endDay} ${endMonth}`;
};

export const formatPostalCode = (value?: string): string => {
  const digits = String(value ?? '').replace(/\s+/g, '');
  if (!/^\d{5}$/.test(digits)) return String(value ?? '').trim();
  return `${digits.slice(0, 3)} ${digits.slice(3)}`;
};

export const formatTrackingNumber = (value?: string): string => {
  const raw = String(value ?? '').replace(/\s+/g, '');
  if (!/^\d{6,}$/.test(raw)) return String(value ?? '').trim();
  return raw.replace(/(\d{4})(?=\d)/g, '$1 ');
};

export const formatCardItemCount = (count: unknown, phrases: CardPhrases): string => {
  const num = Number(count);
  if (!Number.isFinite(num) || num <= 0) return '';
  return `${num} ${num === 1 ? phrases.itemSingular : phrases.itemPlural}`;
};

// Smalt hårt blanksteg (U+202F) i stället för Intl:s tusentalsblanksteg, så
// att beloppet aldrig bryts mitt i den trånga summaraden.
const keepTogether = (text: string): string => text.replace(/\s/g, ' ');

// Valutan är orderns egen, inte marknadens: en SEK-order visas som SEK även på
// .de. "20 297:-" är butikens svenska skrivsätt och gäller bara SEK på svenska.
export const formatCardPrice = (
  total: number,
  currency: string | undefined,
  locale: string,
): string => {
  const code = String(currency ?? '')
    .trim()
    .toUpperCase();
  if (locale === 'sv-SE' && (!code || code === 'SEK')) {
    return `${keepTogether(new Intl.NumberFormat('sv-SE').format(Math.round(total)))}:-`;
  }
  const decimals = Number.isInteger(total) ? 0 : 2;
  const digits = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  if (!code) return keepTogether(new Intl.NumberFormat(locale, digits).format(total));
  try {
    return keepTogether(
      new Intl.NumberFormat(locale, { style: 'currency', currency: code, ...digits }).format(total),
    );
  } catch {
    return keepTogether(`${new Intl.NumberFormat(locale, digits).format(total)} ${code}`);
  }
};

export const updatedLabel = (
  updatedAt: string | undefined,
  nowMs: number,
  phrases: CardPhrases,
): string => {
  const updatedMs = Date.parse(String(updatedAt ?? ''));
  if (!Number.isFinite(updatedMs)) return '';
  return nowMs - updatedMs < 2 * 60 * 60 * 1000
    ? phrases.updatedJustNow
    : `${phrases.updated} ${formatCardDate(updatedAt)}`;
};

export const safeHttpUrl = (value: unknown): string =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim()) ? value.trim() : '';

export const isLegacyListItem = (item: { kind?: string }): boolean => !item.kind;

// "Återbetalt 1 200:-" — beloppet bara när statusen ÄR återbetald, samma regex
// som widgeten, så att en mottagen retur aldrig visar ett belopp som inte betalats.
export const returnSumLabel = (
  statusLabel: string | undefined,
  refundAmount: unknown,
  currency: string | undefined,
  locale: string,
): string => {
  const status = String(statusLabel ?? '').trim();
  const amount = Number(refundAmount);
  const showAmount =
    Number.isFinite(amount) &&
    amount > 0 &&
    /återbetal|refund|hyvit|terugbet|erstatt|rembours|reembols|rimbors/i.test(status);
  return status
    ? `${status}${showAmount ? ` ${formatCardPrice(amount, currency, locale)}` : ''}`
    : '';
};

// Nyttolasten kommer som JSON ur en modellbubbla: ett fält som inte är en lista
// ska ge ett kort utan sektionen, inte en krasch mitt i chatten.
export const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

type EventLike = { timestamp?: string; description?: string; location?: string };

// Samma filter som widgetens händelserader: en rad utan både tid och text ritas
// inte, och finns ingen rad kvar ritas inte heller rubriken.
export const visibleEvents = <T extends EventLike>(events: unknown): T[] =>
  asArray<T | null>(events).filter((event): event is T => {
    if (!event || typeof event !== 'object') return false;
    const time = formatCardDate(event.timestamp) || formatCardTime(event.timestamp);
    const text = [event.description, event.location].some((part) => String(part ?? '').trim());
    return Boolean(time || text);
  });
